import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useData } from '../context/DataContext';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

const { 
  FiArrowLeft, FiPhone, FiMail, FiCalendar, FiActivity, 
  FiSettings, FiUsers, FiCheckCircle, FiChevronLeft, 
  FiChevronRight, FiX, FiZoomIn 
} = FiIcons;

const VehicleDetailPage = () => {
  const { id } = useParams();
  const { vehicles, addInquiry } = useData();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);

  const vehicle = vehicles.find(v => v.id === parseInt(id));

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-600 mb-4">Vehículo no encontrado</h2>
          <Link to="/catalogo" className="text-[#005c91] hover:text-[#3498db] font-medium">
            Volver al catálogo
          </Link>
        </div>
      </div>
    );
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatKilometers = (km) => {
    return new Intl.NumberFormat('es-ES').format(km) + ' km';
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === vehicle.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? vehicle.images.length - 1 : prev - 1
    );
  };

  const onSubmit = (data) => {
    const inquiry = {
      ...data,
      vehicleId: vehicle.id,
      vehicleName: `${vehicle.brand} ${vehicle.model} ${vehicle.version}`
    };
    
    addInquiry(inquiry);
    toast.success('Consulta enviada correctamente. Te contactaremos pronto.');
    reset();
    setShowContactForm(false);
  };

  const relatedVehicles = vehicles
    .filter(v => v.id !== vehicle.id && v.brand === vehicle.brand && v.status === 'active')
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-[#005c91]">Inicio</Link>
            <span className="text-gray-300">/</span>
            <Link to="/catalogo" className="text-gray-500 hover:text-[#005c91]">Catálogo</Link>
            <span className="text-gray-300">/</span>
            <span className="text-[#005c91] font-medium">{vehicle.brand} {vehicle.model}</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Link
          to="/catalogo"
          className="inline-flex items-center space-x-2 text-[#005c91] hover:text-[#3498db] mb-6 font-medium"
        >
          <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          <span>Volver al catálogo</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div>
            <div className="relative">
              <div className="aspect-w-16 aspect-h-12 bg-gray-200 rounded-lg overflow-hidden">
                <img 
                  src={vehicle.images[currentImageIndex] || 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=800&h=600&fit=crop'}
                  alt={`${vehicle.brand} ${vehicle.model}`}
                  className="w-full h-96 object-cover cursor-pointer"
                  onClick={() => setShowImageModal(true)}
                />
              </div>
              
              {/* Navigation Arrows */}
              {vehicle.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all"
                  >
                    <SafeIcon icon={FiChevronLeft} className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all"
                  >
                    <SafeIcon icon={FiChevronRight} className="w-6 h-6" />
                  </button>
                </>
              )}

              {/* Zoom Icon */}
              <button
                onClick={() => setShowImageModal(true)}
                className="absolute top-4 right-4 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all"
              >
                <SafeIcon icon={FiZoomIn} className="w-5 h-5" />
              </button>
            </div>

            {/* Thumbnail Gallery */}
            {vehicle.images.length > 1 && (
              <div className="flex space-x-2 mt-4 overflow-x-auto">
                {vehicle.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 ${
                      currentImageIndex === index ? 'border-[#005c91]' : 'border-gray-200'
                    }`}
                  >
                    <img 
                      src={image}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Vehicle Details */}
          <div>
            <div className="bg-white rounded-lg shadow-lg p-8">
              {/* Header */}
              <div className="border-b border-gray-200 pb-6 mb-6">
                <h1 className="text-3xl font-bold text-[#2c3e50] mb-2">
                  {vehicle.brand} {vehicle.model}
                </h1>
                <p className="text-xl text-gray-600 mb-4">{vehicle.version}</p>
                <div className="text-4xl font-bold text-[#27ae60]">
                  {formatPrice(vehicle.price)}
                </div>
              </div>

              {/* Key Specs */}
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiCalendar} className="w-6 h-6 text-[#005c91]" />
                  <div>
                    <div className="text-sm text-gray-500">Año</div>
                    <div className="font-semibold">{vehicle.year}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiActivity} className="w-6 h-6 text-[#005c91]" />
                  <div>
                    <div className="text-sm text-gray-500">Kilómetros</div>
                    <div className="font-semibold">{formatKilometers(vehicle.kilometers)}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiSettings} className="w-6 h-6 text-[#005c91]" />
                  <div>
                    <div className="text-sm text-gray-500">Combustible</div>
                    <div className="font-semibold">{vehicle.fuel}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiSettings} className="w-6 h-6 text-[#005c91]" />
                  <div>
                    <div className="text-sm text-gray-500">Transmisión</div>
                    <div className="font-semibold">{vehicle.transmission}</div>
                  </div>
                </div>
              </div>

              {/* Contact Buttons */}
              <div className="space-y-4 mb-8">
                <a
                  href={`https://wa.me/34610701517?text=Hola, estoy interesado en el ${vehicle.brand} ${vehicle.model} ${vehicle.version} (${vehicle.year})`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-[#25d366] hover:bg-green-600 text-white py-4 px-6 rounded-lg font-semibold text-center transition-colors flex items-center justify-center space-x-2"
                >
                  <span>Contactar por WhatsApp</span>
                </a>
                <div className="grid grid-cols-2 gap-4">
                  <a
                    href="tel:610701517"
                    className="bg-[#005c91] hover:bg-[#3498db] text-white py-3 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiPhone} className="w-5 h-5" />
                    <span>Llamar</span>
                  </a>
                  <button
                    onClick={() => setShowContactForm(true)}
                    className="bg-[#e67e22] hover:bg-orange-600 text-white py-3 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiMail} className="w-5 h-5" />
                    <span>Consultar</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">
          {/* Technical Specifications */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-[#2c3e50] mb-6">Especificaciones Técnicas</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="border-b border-gray-200 py-3">
                <div className="text-sm text-gray-500">Marca</div>
                <div className="font-semibold">{vehicle.brand}</div>
              </div>
              <div className="border-b border-gray-200 py-3">
                <div className="text-sm text-gray-500">Modelo</div>
                <div className="font-semibold">{vehicle.model}</div>
              </div>
              <div className="border-b border-gray-200 py-3">
                <div className="text-sm text-gray-500">Versión</div>
                <div className="font-semibold">{vehicle.version}</div>
              </div>
              <div className="border-b border-gray-200 py-3">
                <div className="text-sm text-gray-500">Año</div>
                <div className="font-semibold">{vehicle.year}</div>
              </div>
              <div className="border-b border-gray-200 py-3">
                <div className="text-sm text-gray-500">Kilómetros</div>
                <div className="font-semibold">{formatKilometers(vehicle.kilometers)}</div>
              </div>
              <div className="border-b border-gray-200 py-3">
                <div className="text-sm text-gray-500">Combustible</div>
                <div className="font-semibold">{vehicle.fuel}</div>
              </div>
              <div className="border-b border-gray-200 py-3">
                <div className="text-sm text-gray-500">Transmisión</div>
                <div className="font-semibold">{vehicle.transmission}</div>
              </div>
              <div className="border-b border-gray-200 py-3">
                <div className="text-sm text-gray-500">Potencia</div>
                <div className="font-semibold">{vehicle.power} CV</div>
              </div>
              <div className="border-b border-gray-200 py-3">
                <div className="text-sm text-gray-500">Puertas</div>
                <div className="font-semibold">{vehicle.doors}</div>
              </div>
              <div className="border-b border-gray-200 py-3">
                <div className="text-sm text-gray-500">Color</div>
                <div className="font-semibold">{vehicle.color}</div>
              </div>
              <div className="border-b border-gray-200 py-3">
                <div className="text-sm text-gray-500">Propietarios</div>
                <div className="font-semibold">{vehicle.owners}</div>
              </div>
              <div className="border-b border-gray-200 py-3">
                <div className="text-sm text-gray-500">ITV</div>
                <div className="font-semibold">{new Date(vehicle.itv).toLocaleDateString('es-ES')}</div>
              </div>
            </div>
          </div>

          {/* Equipment and Description */}
          <div className="space-y-8">
            {/* Equipment */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-[#2c3e50] mb-6">Equipamiento</h2>
              <div className="grid grid-cols-1 gap-3">
                {vehicle.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-[#27ae60]" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-[#2c3e50] mb-6">Descripción</h2>
              <p className="text-gray-600 leading-relaxed">{vehicle.description}</p>
            </div>
          </div>
        </div>

        {/* Related Vehicles */}
        {relatedVehicles.length > 0 && (
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-[#2c3e50] mb-8 text-center">
              Vehículos Similares
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedVehicles.map((relatedVehicle) => (
                <motion.div
                  key={relatedVehicle.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <VehicleCard vehicle={relatedVehicle} />
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <SafeIcon icon={FiX} className="w-8 h-8" />
            </button>
            <img 
              src={vehicle.images[currentImageIndex]}
              alt={`${vehicle.brand} ${vehicle.model}`}
              className="max-w-full max-h-full object-contain"
            />
            {vehicle.images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
                >
                  <SafeIcon icon={FiChevronLeft} className="w-12 h-12" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300"
                >
                  <SafeIcon icon={FiChevronRight} className="w-12 h-12" />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-[#2c3e50]">Consultar Vehículo</h3>
              <button
                onClick={() => setShowContactForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="font-semibold">{vehicle.brand} {vehicle.model}</div>
              <div className="text-sm text-gray-600">{vehicle.version} - {vehicle.year}</div>
              <div className="text-lg font-bold text-[#27ae60]">{formatPrice(vehicle.price)}</div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  {...register('name', { required: 'El nombre es obligatorio' })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  {...register('email', { 
                    required: 'El email es obligatorio',
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'Email no válido'
                    }
                  })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono *
                </label>
                <input
                  {...register('phone', { required: 'El teléfono es obligatorio' })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                />
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mensaje
                </label>
                <textarea
                  {...register('message')}
                  rows={4}
                  placeholder="Cuéntanos qué te interesa de este vehículo..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#005c91] focus:border-transparent"
                />
              </div>

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowContactForm(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-3 px-4 rounded-lg font-semibold transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-[#005c91] hover:bg-[#3498db] text-white py-3 px-4 rounded-lg font-semibold transition-colors"
                >
                  Enviar Consulta
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Import VehicleCard component here or define it inline
const VehicleCard = ({ vehicle }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
      <div className="relative">
        <img 
          src={vehicle.images[0] || 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=400&h=250&fit=crop'}
          alt={`${vehicle.brand} ${vehicle.model}`}
          className="w-full h-48 object-cover"
        />
        <div className="absolute top-4 right-4">
          <span className="bg-[#27ae60] text-white px-3 py-1 rounded-full text-sm font-semibold">
            {formatPrice(vehicle.price)}
          </span>
        </div>
      </div>
      <div className="p-6">
        <h3 className="text-xl font-bold text-[#2c3e50] mb-2">
          {vehicle.brand} {vehicle.model}
        </h3>
        <p className="text-gray-600 mb-4">{vehicle.version}</p>
        <Link
          to={`/vehiculo/${vehicle.id}`}
          className="w-full bg-[#005c91] hover:bg-[#3498db] text-white py-3 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center"
        >
          Ver Detalles
        </Link>
      </div>
    </div>
  );
};

export default VehicleDetailPage;